import { Component, ViewEncapsulation, Input, EventEmitter, HostBinding, OnInit, OnDestroy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor } from '@angular/common';
import { Subscription } from 'rxjs';

import { DatacenterType } from '../../../models/datacenter-aggrid-view';
import { EMPTYSPACE, UNDERSCORE } from '../../../utils/constant';
import { Document, DocumentLine, OrderNumbers } from '../../../../commercial-documents/models/document';
import { DocumentService } from '../../../../commercial-documents/services/documents.service';
import { CUSTOMER } from '../../../models/settings';
import { ORDER_NUMBER_OPTIONS } from '../../../models/list';
import { SafeUrlPipe } from '../../../pipes/safeurl.pipe';
import { NumberPipe } from '../../../pipes/number.pipe';
import { DatePipe } from '../../../pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../utils/loading-spinner.component';
import { OrderService } from '../../../../order/services/order.service';
import { BatchesService } from '../../../../order/services/batches.service';
import { DatacenterDefaultConfiguration } from '../../../datacenter/datacenter-default-configuration';
import { ScheduleBusinessOrder } from '../../../../order/models/schedule-business-order';
import { ORDER_REDIRECT_URL,
	ScheduleBusinessOrderService } from '../../../../order/services/schedule-business-order.service';
import { UserService } from '../../../services/user.service';

@Component({
	selector: 'copo-result-invoice-card',
	templateUrl: './result-card-invoice.component.html',
	styleUrl: './result-card-invoice.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		MatIconModule,
		MatRippleModule,

		MatButtonModule,
		TranslateModule,
		RouterLink,
		NgFor,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		SafeUrlPipe
	]
})
export class ResultCardInvoiceComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	@Input() document?: Document;
	@Input() orderDisplay?: keyof typeof ORDER_NUMBER_OPTIONS;
	@Input() getUniqueOrderName?: (document: Document) => OrderNumbers[];
	readonly createFilter = new EventEmitter<{ document: Document; filterType: DatacenterType }>();
	readonly UNDERSCORE = UNDERSCORE;
	readonly EMPTYSPACE = EMPTYSPACE;
	orderLable = '';
	orderNumbers: OrderNumbers[] = [];
	scheduleBusinessOrderRequiredFields: (keyof ScheduleBusinessOrder)[] = ['supplierOrderType'];
	scheduleBusinessOrder?: Subscription;
	hasSBOrderService = false;

	constructor(
		private readonly documentService: DocumentService,
		private readonly orderService: OrderService,
		private readonly batchService: BatchesService,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly scheduleBusinessOrderService: ScheduleBusinessOrderService,
		private readonly userService: UserService
	) {
		const { user } = this.userService;
		this.hasSBOrderService = user.hasServiceParameterEqualsTo('PORDT', 'SBOrder', 'True');
	}

	ngOnInit() {
		this.orderLable = this.orderDisplay === CUSTOMER
			? 'commercial-documents.customer-order-number'
			: 'commercial-documents.supplier-order-number';
		if (this.getUniqueOrderName === undefined || !this.document) {
			return;
		}
		this.orderNumbers = this.getUniqueOrderName(this.document);
		if (this.hasSBOrderService) {
			this.scheduleBusinessOrderService.setRedirectionLink(
				this.document,
				this.scheduleBusinessOrderRequiredFields
			);
		} else {
			this.document.isSbOrder = false;
			this.document.docNavigationLink = ORDER_REDIRECT_URL;
		}
	}

	downloadDocument(id: number, filename: string) {
		return this.documentService.getDownloadDocumentUrl(id, filename);
	}

	getDocumentURL(id: number, filename: string) {
		return this.documentService.getViewDocumentUrl(id, filename);
	}

	totalCalculation(documentLines: DocumentLine[], type: string) {
		const orderReference = new Set<string>();
		let sum = 0;
		switch (type) {
		case 'invoiceAmount':
			for (const line of documentLines) {
				const ref = !line.supplierOrderReferenceItem
					? `${ line.supplierOrderReference }/${ line.dispatchAdviceNumber }`
					: `${ line.supplierOrderReference }/${ line.supplierOrderReferenceItem }/${
						line.dispatchAdviceNumber }`;
				if (!orderReference.has(ref)) {
					orderReference.add(ref);
					sum += line.invoiceAmount;
				}
			}
			break;
		case 'invoicedQuantity':
			for (const line of documentLines) {
				const ref = !line.supplierOrderReferenceItem
					? `${ line.supplierOrderReference }/${ line.dispatchAdviceNumber }`
					: `${ line.supplierOrderReference }/${ line.supplierOrderReferenceItem }/${
						line.dispatchAdviceNumber }`;
				if (!orderReference.has(ref)) {
					orderReference.add(ref);
					sum += line.invoicedQuantity;
				}
			}
			break;
		}
		return sum;
	}

	formatInvoicedQuantity(document: Document) {
		return document.invoicedQuantity;
	}

	async redirectToBatchDatacenter(document: Document) {
		await this.batchService.batchDatacenterFilter(document, this.defaultConfiguration.getDefaultView('batch'));
	}

	async redirectToOrderDatacenter(document: Document) {
		if (document.isSbOrder) {
			await this.scheduleBusinessOrderService.sbOrderDatacenterFilter(
				document,
				this.defaultConfiguration.getDefaultView('schedule-business-order')
			);
			return;
		}
		await this.orderService.orderDatacenterFilter(
			document,
			this.defaultConfiguration.getDefaultView('order')
		);
	}

	ngOnDestroy() {
		this.scheduleBusinessOrder?.unsubscribe();
	}
}
